/**
 * #502: when the map draws a route but has no live vessel marker, say WHY
 * instead of leaving a blank dashed line the customer can't interpret.
 *
 * The backend already knows the reason and surfaces it on `position.status`
 * (`ok` | `missing_position` | `untrackable_no_identity` | `destination_mismatch`).
 * That wins when present; otherwise we fall back to the shipment lifecycle
 * status (always available on the FE) to tell "awaiting departure" from
 * "arrived" from "we lost the fix".
 */

const ARRIVED = new Set([
  'arrived_destination_port',
  'arrived_destination',
  'delivered',
  'ready_for_collection',
  'ready_to_collect',
]);

const PRE_DEPARTURE = new Set([
  'quote',
  'booking_pending',
  'booked',
  'pending',
  'created',
  'offer_received',
  'offers_received',
  'transporter_assigned',
  'assigned',
  'enroute_to_pickup',
  'vehicle_picked_up',
  'picked_up',
  'in_storage',
  'at_origin_port',
  'at_port',
  'clearing_customs',
]);

/**
 * Short human reason for a missing vessel marker.
 * @param {string} positionStatus backend `position.status`
 * @param {string} shipmentStatus canonical shipment/detailed status
 * @returns {string}
 */
export function describeMissingPosition(positionStatus, shipmentStatus) {
  const pos = String(positionStatus || '').toLowerCase();
  const status = String(shipmentStatus || '').toLowerCase();

  // Terminal states — a live marker is not expected once the leg is over.
  if (ARRIVED.has(status)) return 'Arrived — live tracking ended';

  // A precise, backend-provided reason wins over the coarse lifecycle guess.
  if (pos === 'untrackable_no_identity') return 'No vessel identity yet';
  if (pos === 'destination_mismatch') return 'Live position unavailable';

  // Hasn't sailed yet — the dashed line is the planned route.
  if (PRE_DEPARTURE.has(status)) return 'Awaiting departure';

  // Underway (or unknown) but no fix right now.
  return 'Live position unavailable';
}
